<script lang="ts" setup>
  const props = withDefaults(
    defineProps<{
      as?: 'button' | 'submit'
      disabled?: boolean
      icon?: boolean
      size?: 'medium' | 'small' | 'tiny'
      square?: boolean
      type?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'ghost'
    }>(),
    {
      type: 'primary',
      size: 'medium',
      disabled: false,
      icon: false,
      square: false,
    },
  )

  const buttonClass = computed(() => {
    return [
      'u-button',
      `u-button--${props.type}`,
      `u-button--${props.size}`,
      {
        'u-button--icon': props.icon,
        'u-button--square': props.square,
      },
    ]
  })
</script>

<template>
  <QBtn
    :class="buttonClass"
    :disable="disabled"
    no-caps
    unelevated
    :type="as"
  >
    <div class="u-button__content">
      <slot name="prefix" />
      <slot />
      <slot name="suffix" />
    </div>
  </QBtn>
</template>

<style lang="scss" scoped>
  .u-button__content {
    @include flex-container(row, center, center);
    gap: 16px;
  }
  .u-button {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    transition: all 0.2s ease;
    display: block;
    border-radius: var(--radius-full);

    // Sizes
    &--medium {
      padding: 8px 16px;
      font-size: 14px;
      line-height: 24px;
      font-weight: 600;

      &.u-button--icon {
        width: 40px;
        min-width: 40px;
        padding: 8px 16px;
      }
      &.u-button--square {
        width: 40px;
        height: 40px;
        padding: 0;
      }
    }

    &--small {
      padding: 0 20px;
      font-size: 12px;
      line-height: 20px;
      font-weight: 600;
      gap: 8px;
      height: 32px;
      min-height: 32px;

      &.u-button--icon {
        width: 32px;
        min-width: 32px;
        padding: 8px 16px;
      }
      &.u-button--square {
        width: 32px;
        height: 32px;
        padding: 0;
      }
    }

    &--tiny {
      padding: 8px 10px;
      font-size: 10px;
      line-height: 16px;
      font-weight: 600;
      gap: 2px;

      &.u-button--icon {
        width: 20px;
        min-width: 20px;
        padding: 8px 10px;
      }
      &.u-button--square {
        width: 20px;
        height: 20px;
        padding: 0;
      }
    }

    // Types - используем семантические токены
    &--primary {
      background: var(--button-button-primary);
      color: var(--button-button-color-primary);
      border: none;

      &:hover:not(:disabled) {
        background: var(--button-button-primary-hover);
      }

      &:disabled {
        background: var(--button-button-disabled);
        color: var(--text-tertiary);
      }
    }

    &--secondary {
      background: var(--button-button-secondary);
      color: var(--button-button-color-secondary);
      border: none;

      &:hover:not(:disabled) {
        background: var(--button-button-secondary-hover);
      }

      &:disabled {
        background: var(--button-button-disabled);
        color: var(--text-tertiary);
      }
    }

    &--tertiary {
      background: var(--button-button-tertiary);
      color: var(--text-content);
      border: none;

      &:hover:not(:disabled) {
        background: var(--button-button-tertiary-hover);
      }

      &:disabled {
        background: var(--button-button-disabled);
        color: var(--text-tertiary);
      }
    }

    &--outline {
      background: var(--button-button-outline);
      color: var(--text-content);
      border: 1px solid var(--border-border-primary);

      &:hover:not(:disabled) {
        background: var(--button-button-tertiary-hover);
        border-color: var(--border-border-primary);
      }

      &:disabled {
        background: transparent;
        color: var(--text-tertiary);
        border-color: var(--border-border-disabled);
      }
    }

    &--ghost {
      background: var(--button-button-ghost);
      color: var(--text-content);
      border: none;

      &:hover:not(:disabled) {
        background: var(--button-button-tertiary-hover);
        color: var(--text-primary);
      }

      &:disabled {
        background: transparent;
        color: var(--text-tertiary);
      }
    }
  }

  :deep(.q-btn__content) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: inherit;
    height: 100%;
  }
</style>
